import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { dashboardService, apiClient } from '../api';

interface User {
  id: string;
  email: string;
  name: string;
  role: string;
}

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  isAuthenticated: boolean;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in from localStorage (Basic Auth)
    const storedUser = localStorage.getItem('user');
    const storedToken = localStorage.getItem('basic_auth_token');
    
    if (storedUser && storedToken) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (error) {
        console.error('Error parsing stored user:', error);
        localStorage.removeItem('user');
        localStorage.removeItem('basic_auth_token');
      }
    }
    setIsLoading(false);
  }, []);

  const login = async (email: string, password: string): Promise<boolean> => {
    setIsLoading(true);
    
    try {
      // Validate credentials format
      if (!email || !password) {
        setIsLoading(false);
        return false;
      }

      // Save Basic Auth credentials first
      apiClient.saveBasicAuth(email, password);
      
      // Test authentication with a simple API call
      try {
        // Try to fetch user details to verify credentials
        await dashboardService.getUserDetails({ page: 1 });
        
        // If successful, save user data
        const userData: User = {
          id: email,
          email: email,
          name: email.split('@')[0], // Use email prefix as name
          role: 'User',
        };
        
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
        setIsLoading(false);
        return true;
      } catch (apiError: any) {
        // If API call fails, credentials are invalid
        apiClient.clearTokens();
        setIsLoading(false);
        return false;
      }
    } catch (error) {
      console.error('Login error:', error);
      setIsLoading(false);
      return false;
    }
  };

  const logout = () => {
    // Clear Basic Auth credentials
    apiClient.clearTokens();
    
    setUser(null);
  };

  const value: AuthContextType = {
    user,
    login,
    logout,
    isAuthenticated: !!user,
    isLoading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

